import { POLARITY_LABELS, type EvaluationPolarity } from '@citizen-walk/shared-types';
import { useId } from 'react';
import { lightenHex, polarityHatchColor } from './barFillStyles';
import { PolarityLegend } from './PolarityLegend';
import { toDonutArcs, donutArcPath, type AreaDonut, type DonutSlice } from './areaDonutSlices';
import { useChartTooltip } from './useChartTooltip';

/**
 * エリアごとの円グラフ (2026-09-18 オーナー要望)。
 *
 * 横棒の積み上げ (`AreaComparisonBarChart`) が「項目どうしの多さ」を比べるのに向くのに対し、
 * こちらは「1 つのエリアの中で何がどれだけを占めるか」を見る。エリア 1 つにつき 1 枚を並べる。
 *
 * 模様は棒グラフと同じ規約 (ポジティブ = 緑の斜線 / 中立 = 灰色の水玉 / ネガティブ = 赤の格子)。
 * CSS のグラデーションは SVG の塗りに使えないため、同じ見た目を SVG の `<pattern>` で作る。
 */

/** 評価の区分ごとの SVG 模様 (棒グラフの CSS と同じ見た目)。 */
function PolarityPatternDefs({
  idPrefix,
  colors,
  polarityColors,
}: {
  idPrefix: string;
  /** 模様を敷く地色 (項目の色を薄くしたもの) の一覧。 */
  colors: string[];
  polarityColors?: Record<EvaluationPolarity, string>;
}) {
  return (
    <defs>
      {colors.map((color, ci) =>
        (['positive', 'neutral', 'negative'] as EvaluationPolarity[]).map((polarity) => {
          const line = polarityHatchColor(polarity, polarityColors);
          const id = `${idPrefix}-${ci}-${polarity}`;
          if (polarity === 'neutral') {
            return (
              <pattern key={id} id={id} width="6" height="6" patternUnits="userSpaceOnUse">
                <rect width="6" height="6" fill={color} />
                <circle cx="3" cy="3" r="1.5" fill={line} />
              </pattern>
            );
          }
          if (polarity === 'negative') {
            // 格子 (縦線 + 横線)。
            return (
              <pattern key={id} id={id} width="6" height="6" patternUnits="userSpaceOnUse">
                <rect width="6" height="6" fill={color} />
                <rect width="1" height="6" fill={line} />
                <rect width="6" height="1" fill={line} />
              </pattern>
            );
          }
          // 右上がりの斜線。
          return (
            <pattern
              key={id}
              id={id}
              width="7"
              height="7"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(-45)"
            >
              <rect width="7" height="7" fill={color} />
              <rect width="7" height="2" fill={line} />
            </pattern>
          );
        }),
      )}
    </defs>
  );
}

/** 項目の色を棒グラフと同じ薄さにする (地色)。 */
const BASE_LIGHTEN: Record<EvaluationPolarity | 'none', number> = {
  positive: 0.6,
  neutral: 0.75,
  negative: 0.5,
  none: 0.55,
};

function sliceBaseColor(slice: DonutSlice): string {
  return lightenHex(slice.color, BASE_LIGHTEN[slice.polarity ?? 'none']);
}

export interface AreaDonutChartProps {
  donuts: AreaDonut[];
  /** 値の単位 (ツールチップ表示用)。 */
  unit?: string;
  polarityLabels?: Record<EvaluationPolarity, string>;
  /** 模様に使う評価の色 (未指定なら既定の 緑 / 赤 / 灰)。 */
  polarityColors?: Record<EvaluationPolarity, string>;
  /** 評価で分けているか (凡例の出し分け)。 */
  splitByPolarity?: boolean;
}

const SIZE = 168;
const R = 76;

export function AreaDonutChart({
  donuts,
  unit = '件/地点',
  polarityLabels = POLARITY_LABELS,
  polarityColors,
  splitByPolarity = false,
}: AreaDonutChartProps) {
  const tooltip = useChartTooltip();
  const idPrefix = useId().replace(/:/g, '');

  // 模様の地色は項目ごとに変わるため、出てくる色をすべて `<pattern>` にしておく。
  const baseColors = [...new Set(donuts.flatMap((d) => d.slices.map((s) => sliceBaseColor(s))))];
  const patternIndex = new Map(baseColors.map((c, i) => [c, i]));

  // 凡例は全エリアの項目の和集合 (同じ色・同じ並びで見比べられるように)。
  const legend: { key: string; label: string; color: string }[] = [];
  const seen = new Set<string>();
  for (const d of donuts) {
    for (const s of d.slices) {
      const key = s.key.replace(/__(positive|neutral|negative|none)$/, '');
      const label = s.label.replace(/（.+）$/, '');
      if (seen.has(key)) continue;
      seen.add(key);
      legend.push({ key, label, color: s.color });
    }
  }

  if (donuts.every((d) => d.total <= 0)) {
    return <p className="text-xs text-slate-500">集計できる検出がありません。</p>;
  }

  return (
    <div>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 mb-1">
        <span>色は調査項目:</span>
        {legend.map((l) => (
          <span key={l.key} className="flex items-center gap-1">
            <span
              className="inline-block h-3 w-3 rounded-sm border border-slate-300"
              style={{ backgroundColor: lightenHex(l.color, BASE_LIGHTEN.none) }}
            />
            {l.label}
          </span>
        ))}
      </p>
      {splitByPolarity && <PolarityLegend className="mb-2" polarityLabels={polarityLabels} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {donuts.map((donut) => {
          const arcs = toDonutArcs(donut.slices);
          return (
            <div key={donut.index} className="rounded border border-slate-200 p-2">
              <p className="flex items-center gap-1 text-xs font-semibold text-slate-700 mb-1">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: donut.color }}
                />
                <span className="truncate">{donut.name}</span>
                <span className="ml-auto font-normal text-slate-500">{donut.pointCount}地点</span>
              </p>
              {arcs.length === 0 ? (
                <p className="text-[11px] text-slate-400 py-6 text-center">検出なし</p>
              ) : (
                <svg
                  viewBox={`0 0 ${SIZE} ${SIZE}`}
                  className="w-full h-auto"
                  role="img"
                  aria-label={`${donut.name}の構成比`}
                >
                  <PolarityPatternDefs
                    idPrefix={`${idPrefix}-${donut.index}`}
                    colors={baseColors}
                    polarityColors={polarityColors}
                  />
                  {arcs.map((arc) => {
                    const base = sliceBaseColor(arc);
                    const fill = arc.polarity
                      ? `url(#${idPrefix}-${donut.index}-${patternIndex.get(base)}-${arc.polarity})`
                      : base;
                    return (
                      <path
                        key={arc.key}
                        d={donutArcPath(SIZE / 2, SIZE / 2, R, arc.startAngle, arc.endAngle)}
                        fill={fill}
                        stroke="#ffffff"
                        strokeWidth={1}
                        onMouseMove={(e) =>
                          tooltip.show(e, donut.name, [
                            {
                              label: arc.label,
                              value: `${arc.value.toFixed(2)}${unit}（${Math.round(arc.ratio * 100)}%）`,
                              color: arc.color,
                            },
                          ])
                        }
                        onMouseLeave={tooltip.hide}
                        onTouchStart={(e) =>
                          tooltip.show(e, donut.name, [
                            {
                              label: arc.label,
                              value: `${arc.value.toFixed(2)}${unit}（${Math.round(arc.ratio * 100)}%）`,
                              color: arc.color,
                            },
                          ])
                        }
                      />
                    );
                  })}
                  {/* 中心の白抜き (ドーナツ) + 合計。 */}
                  <circle cx={SIZE / 2} cy={SIZE / 2} r={R * 0.5} fill="#ffffff" />
                  <text
                    x={SIZE / 2}
                    y={SIZE / 2 - 2}
                    textAnchor="middle"
                    className="fill-slate-700"
                    style={{ fontSize: 15, fontWeight: 700 }}
                  >
                    {donut.total.toFixed(1)}
                  </text>
                  <text
                    x={SIZE / 2}
                    y={SIZE / 2 + 12}
                    textAnchor="middle"
                    className="fill-slate-400"
                    style={{ fontSize: 9 }}
                  >
                    {unit}
                  </text>
                </svg>
              )}
            </div>
          );
        })}
      </div>
      {tooltip.node}
    </div>
  );
}
